import { UserRoundPlus } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { assigneeInitials } from '@/lib/assignedTo'
import { cn } from '@/lib/utils'
import type { ProcedureAssignee } from '@/types/cgt'

const UNASSIGNED = '__unassigned__'

interface Props {
  users: ProcedureAssignee[]
  /** The current assignee, or null when unassigned (or in a mixed state). */
  value: ProcedureAssignee | null
  /** Trigger text while value is null — 'Unassigned' by default, pass 'Multiple'
   *  for the mixed procedure-level state. Ignored by the avatar variant. */
  placeholder?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
  /** 'select' renders the full-width dropdown trigger; 'avatar' renders a
   *  compact round button — initials when assigned, an assign icon when not —
   *  with the full name in the hover tooltip. */
  variant?: 'select' | 'avatar'
  /** The signed-in user's id, so they sort first and read as "(me)". */
  meId?: string | null
  /** null = unassign. Fired on every pick — the caller guards no-op changes. */
  onChange: (assigneeId: string | null) => void
}

/**
 * Inline Assign To picker (searchable dropdown) shared by the procedure header,
 * the procedure sidebar rows and the procedures table.
 */
export function AssigneeSelect({
  users,
  value,
  placeholder = 'Unassigned',
  disabled,
  className,
  buttonClassName,
  variant = 'select',
  meId,
  onChange,
}: Props) {
  // Yourself first, and named as such. Claiming your own work is the common
  // case — the roster is every enabled user on the instance, so self-assigning
  // otherwise means hunting for your own name in a long alphabetical list.
  const ordered = meId ? [...users.filter(u => u.id === meId), ...users.filter(u => u.id !== meId)] : users
  const options = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...ordered.map(u => ({ value: u.id, label: u.id === meId ? `${u.name} (me)` : u.name })),
  ]
  // A current assignee who has since left the roster still renders by name.
  if (value && !users.some(u => u.id === value.id)) {
    options.push({ value: value.id, label: value.name })
  }

  const shared = {
    // In the mixed state (value null, placeholder ≠ Unassigned) nothing is
    // selected, so the trigger falls back to the placeholder text.
    value: value?.id ?? (placeholder === 'Unassigned' ? UNASSIGNED : ''),
    onChange: (v: string) => onChange(v === UNASSIGNED ? null : v),
    options,
    disabled,
    className,
  }

  if (variant === 'avatar') {
    return (
      <SearchableSelect
        {...shared}
        trigger={
          value ? assigneeInitials(value.name) : <UserRoundPlus className='h-3.5 w-3.5' />
        }
        triggerTitle={
          value ? `Assigned to ${value.name} — click to change` : 'Assign this form'
        }
        panelWidth={240}
        buttonClassName={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-50',
          value
            ? 'bg-blue-600 text-[10px] font-semibold text-white hover:bg-blue-700'
            : 'border border-dashed border-blue-400 text-blue-600 hover:bg-blue-50',
          buttonClassName,
        )}
      />
    )
  }

  return <SearchableSelect {...shared} placeholder={placeholder} buttonClassName={buttonClassName} />
}
